import { AbstractTransport } from '@central-products/abstract-transport-bus';
import { GenericEvent, ServiceMeta } from '@central-products/transport-bus-common';
import { generateMessageId, publishMessage, subscribeToMessage, unsubscribeFromMessage } from './utils/message';
import {
  addEventSubscription,
  addMethodSubscriptions,
  removeEventSubscription,
  removeMethodSubscriptions,
} from './utils/subscriptions';
import { getEventTopic, getRequestTopic, getResponseTopic } from './utils/topic';

export default class MessageBusTransport extends AbstractTransport {
  private static instance: MessageBusTransport;

  public static getInstance(): MessageBusTransport {
    if (!MessageBusTransport.instance) {
      MessageBusTransport.instance = new MessageBusTransport();
    }

    return MessageBusTransport.instance;
  }

  private constructor() {
    super();
  }

  protected callMethod({ domain, serviceName, name, payload }: GenericEvent): Promise<object> {
    const requestTopic: string = getRequestTopic(domain, serviceName, name);
    const responseTopic: string = getResponseTopic(domain, serviceName, name);
    const requestId = generateMessageId();

    if (!publishMessage(requestTopic, payload, requestId)) {
      return Promise.reject(new Error(`${serviceName}.${name} not available`));
    }

    return new Promise<any>((resolve, reject) => {
      const subscriptionId = subscribeToMessage(
        responseTopic,
        (data: any) => {
          unsubscribeFromMessage(subscriptionId);

          if (data && data.error) {
            reject(data);
          } else {
            resolve(data);
          }
        },
        requestId
      );
    });
  }

  protected onBind({ domain, serviceName, methods }: ServiceMeta): void {
    const addSubscriptions = () =>
      methods.map(({ name: methodName }) => {
        const requestTopic = getRequestTopic(domain, serviceName, methodName);
        const responseTopic = getResponseTopic(domain, serviceName, methodName);

        return subscribeToMessage(requestTopic, (data: any, id: string) => {
          const handleCall = (resp: GenericEvent) => publishMessage(responseTopic, resp.payload, id);
          super
            .callMethod({ serviceName, name: methodName, domain, payload: data })
            .then(handleCall)
            .catch(handleCall);
        });
      });

    addMethodSubscriptions(domain, serviceName, addSubscriptions);
  }

  protected onUnbind({ domain, serviceName }: ServiceMeta) {
    removeMethodSubscriptions(domain, serviceName, unsubscribeFromMessage);
  }

  protected onEventListenerAdded(domain: string, serviceName: string, name: string) {
    const eventTopic = getEventTopic(domain, serviceName, name);
    const addSubscription = () =>
      subscribeToMessage(eventTopic, (data) => {
        super.notifySubscribers({ serviceName, name, domain, payload: data });
      });

    addEventSubscription(eventTopic, addSubscription);
  }

  protected onEventListenerRemoved(domain: string, serviceName: string, name: string) {
    const eventTopic = getEventTopic(domain, serviceName, name);
    removeEventSubscription(eventTopic, unsubscribeFromMessage);
  }

  protected notifySubscribers({ domain, serviceName, name, payload }: GenericEvent): void {
    const eventTopic = getEventTopic(domain, serviceName, name);
    publishMessage(eventTopic, payload);
  }
}
